import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EntityCardSize, Link, ModuleBase } from '@/components';
import { LinkModel, ModuleBaseModel, ModuleSectionPropsModel, ReportSearchModel } from '@/types';
import Flicking, { ChangedEvent } from '@egjs/react-flicking';
import tailwindConfig from '@/tailwind.screens';
import ReportCard from '@/components/report/ReportCard';
import { AutoPlay } from '@egjs/flicking-plugins';
import ArrowRightLight from '@/assets/arrowRightLight.svg';
import { useTranslations } from 'next-intl';

type HomeHeroModuleModel = ModuleBaseModel<
  {
    featuredReports: ReportSearchModel[];
    hasDoneSetup: boolean;
    title: string;
    userSetupLink: LinkModel;
  } & ModuleSectionPropsModel
>;

const HomeHeroModule = (data: HomeHeroModuleModel) => {
  const { featuredReports, hasDoneSetup } = data.props;
  const t = useTranslations();
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const [realIndex, setRealIndex] = useState<number>(0);
  const carousel = useRef<Flicking>(null);

  const itemsList = useMemo(() => {
    if (!featuredReports || featuredReports.length === 0) {
      return [];
    }
    if (featuredReports.length > 0 && featuredReports.length < 8) {
      let newList = [...featuredReports];
      while (newList.length < 8) {
        newList = [...newList, ...featuredReports];
      }
      return newList;
    }
    return featuredReports;
  }, [featuredReports]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < parseInt(tailwindConfig.screens.md, 10));
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onFlickingMove = () => {
    const flicking = carousel.current;
    if (flicking) {
      const panels = flicking.visiblePanels;

      panels.forEach((panel) => {
        const progress = panel.outsetProgress;
        const target = panel.element;
        if (document.body.clientWidth >= 600) {
          target.style.transform = `scale(${1 - Math.abs(progress) * 0.25})`;

          if (progress > 0) {
            target.style.setProperty('-webkit-transform-origin-x', '0');
          } else {
            target.style.setProperty('-webkit-transform-origin-x', '100%');
          }
        } else {
          target.style.transform = `scale(1)`;
          target.style.setProperty('-webkit-transform-origin-x', 'unset');
        }
      });
    }
    //console.log('Flicking moved');
  };

  const onCarouselChanged = (event: ChangedEvent<Flicking>) => {
    setCurrentSlide(event.index);

    let realIndex = event.index;

    if (featuredReports && featuredReports.length > 0) {
      while (realIndex >= featuredReports.length) {
        realIndex -= featuredReports.length;
      }
    }

    setRealIndex(realIndex);
  };

  const reportCardClick = (e: React.MouseEvent, data: ReportSearchModel, clickedIndex: number) => {
    if (currentSlide !== clickedIndex) {
      if (!carousel.current?.animating) {
        let move = 'PREV';
        if (clickedIndex > currentSlide) {
          move = 'NEXT';

          if (currentSlide === 0 && clickedIndex === itemsList.length - 1) {
            move = 'PREV';
          }
        } else if (clickedIndex === 0 && currentSlide === itemsList.length - 1) {
          move = 'NEXT';
        }

        carousel.current?.moveTo(clickedIndex, undefined, move).catch(() => {});
      }
      e.preventDefault();
    }
  };

  const onPrevClick = () => {
    if (carousel.current && !carousel.current.animating) {
      carousel.current.prev().catch(() => {});
    }
  };
  const onNextClick = () => {
    if (carousel.current && !carousel.current.animating) {
      carousel.current.next().catch(() => {});
    }
  };

  const plugins = [
    new AutoPlay({
      duration: 2500,
      direction: 'NEXT',
      stopOnHover: true,
    }),
  ];

  return (
    <ModuleBase data={data} noPadding className="pb-8 lg:pt-16 lg:pb-9">
      {itemsList && (
        <>
          <Flicking
            ref={carousel}
            plugins={plugins}
            preventClickOnDrag
            preventDefaultOnDrag
            align="center"
            circular
            panelsPerView={isMobile ? 1 : -1}
            onMove={onFlickingMove}
            onReady={onFlickingMove}
            onChanged={onCarouselChanged}
          >
            {itemsList.map((report, index) => (
              <div key={index} className="panel px-0 md:px-3">
                <ReportCard
                  className="report-card-wrapper"
                  data={report}
                  size={isMobile ? EntityCardSize.AUTO : EntityCardSize.LARGE}
                  hideViewed
                  onClick={(e, data) => reportCardClick(e, data, index)}
                />
              </div>
            ))}
          </Flicking>

          <div className="mt-6 flex items-center justify-center">
            <button onClick={onPrevClick} className="fill-from-bg mr-4 cursor-pointer opacity-60 hover:opacity-100">
              <ArrowRightLight className="h-8 w-8 rotate-180" />
            </button>
            <span>{realIndex + 1}</span>
            <span className="px-1 opacity-50">/</span>
            <span className="opacity-50">{featuredReports.length}</span>
            <button onClick={onNextClick} className="fill-from-bg ml-4 cursor-pointer opacity-60 hover:opacity-100">
              <ArrowRightLight className="h-8 w-8" />
            </button>
          </div>
        </>
      )}
      {!hasDoneSetup && (
        <div className="container mt-4 flex items-center justify-center gap-4">
          <div className="text-sm lg:text-xl"> {t('homeHero.$customisedstream')}</div>
          <Link
            className="btn secondary !border-black !text-sm !font-normal"
            link={{ href: '/my-account/user-setup-wizard' }}
          >
            {t('homeHero.$setPreferences')}
          </Link>
        </div>
      )}
    </ModuleBase>
  );
};

export default HomeHeroModule;
